/**
 * WorkflowsPage Component
 *
 * Listagem de workflows no padrão-lista do Design System:
 * toolbar 3 zonas (breadcrumb + count · busca centralizada · view-toggle +
 * Filtros popover + primário único), filterbar de chips, bulk bar, duas views
 * (Cards + Lista→cards no mobile) e pastas (FolderList) preservadas.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button, FiltersButton, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, FilterBottomSheet, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import {
  useWorkflows,
  useDeleteWorkflow,
  useActivateWorkflow,
  usePauseWorkflow,
  useDuplicateWorkflow,
  useUpdateWorkflow,
  useRootWorkflowFolders,
  useWorkflowSubfolders,
  useWorkflowFolder,
  useWorkflowFolderAncestors,
  useCreateWorkflowFolder,
  useUpdateWorkflowFolder,
  useDeleteWorkflowFolder,
} from '../hooks';
import { useWorkflowPreferences } from '../hooks/useWorkflowPreferences';
import { FolderList, CreateFolderModal } from '../components';
import { WorkflowCard } from '../components/WorkflowCard';
import { WorkflowListItem } from '../components/WorkflowListItem';
import { ImportWorkflowModal } from '../components/import/ImportWorkflowModal';
import { workflowsApi } from '../services';
import { downloadJson, slugifyFilename } from '../utils/workflowTransfer';
import type { WorkflowResponse, WorkflowStatus, WorkflowQuery } from '../types';
import type { WorkflowFolderResponse, CreateWorkflowFolderRequest } from '@troia-v3/types';
import { WORKFLOW_EXECUTION_STATS_WINDOW_DAYS, WORKFLOW_STATUSES } from '@troia-v3/types';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';

type SortKey = 'name' | 'status' | 'updated';

/** pt-BR labels para o enum de status (filtro + chip). */
const STATUS_FILTER_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  inactive: 'Pausado',
  archived: 'Arquivado',
};

/** Opções do popover de Filtros (Status). '' = Todos. */
const STATUS_FILTER_OPTIONS: Array<{ value: WorkflowStatus | ''; label: string }> = [
  { value: '', label: 'Todos' },
  ...WORKFLOW_STATUSES.map((s) => ({ value: s, label: STATUS_FILTER_LABELS[s] ?? s })),
];

export const WorkflowsPage: React.FC = () => {
  const navigate = useNavigate();
  const { folderId } = useParams<{ folderId: string }>();
  const { viewMode, setViewMode } = useWorkflowPreferences();
  const confirm = useConfirm();

  // Folder state
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);

  useEffect(() => {
    setCurrentFolderId(folderId || null);
  }, [folderId]);

  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<WorkflowFolderResponse | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  // Filters / listing state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WorkflowStatus | ''>('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'updated', dir: 'desc' });
  const limit = 12;

  // Folder data
  // Árvore de pastas de WORKFLOWS (usageType 'automation') — separada da de Skills
  const { data: rootFolders, isLoading: foldersLoading } = useRootWorkflowFolders('automation');
  const { data: subfolders } = useWorkflowSubfolders(currentFolderId || undefined, 'automation');
  const { data: currentFolder } = useWorkflowFolder(currentFolderId || undefined);
  const { data: ancestors } = useWorkflowFolderAncestors(currentFolderId);

  // Folder mutations
  const createFolder = useCreateWorkflowFolder();
  const updateFolder = useUpdateWorkflowFolder();
  const deleteFolder = useDeleteWorkflowFolder();

  const breadcrumb = useMemo(() => {
    if (!currentFolder) return [];
    const path: WorkflowFolderResponse[] = [...(ancestors || []), currentFolder];
    return path;
  }, [currentFolder, ancestors]);

  const displayFolders = useMemo(() => {
    if (currentFolderId && subfolders) return subfolders;
    return rootFolders || [];
  }, [currentFolderId, subfolders, rootFolders]);

  const query: WorkflowQuery = useMemo(
    () => ({
      page,
      limit,
      search: search || undefined,
      status: statusFilter || undefined,
      // Tela de Workflows lista SÓ automations — skills vivem em /automation/skills
      usageType: 'automation',
      folderId: currentFolderId === null ? 'null' : currentFolderId,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
      // U2 / D19: liga os contadores de execução. Custa uma agregação por
      // página no servidor — por isso NENHUM outro consumidor pede (skills e
      // pré-condições de agente ficam de fora). `withStats` também entra na
      // chave de cache e, por ela, `useWorkflows` passa a atualizar a cada 30 s.
      withStats: true,
    }),
    [page, search, statusFilter, currentFolderId]
  );

  const { data, isLoading, error } = useWorkflows(query);

  // Workflow mutations
  const deleteWorkflow = useDeleteWorkflow();
  const activateWorkflow = useActivateWorkflow();
  const pauseWorkflow = usePauseWorkflow();
  const duplicateWorkflow = useDuplicateWorkflow();
  const updateWorkflow = useUpdateWorkflow();

  const workflows: WorkflowResponse[] = useMemo(() => data?.items || [], [data]);

  // Sort client-side na página carregada (backend só ordena por updatedAt desc).
  const sortedWorkflows = useMemo(() => {
    const copy = [...workflows];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'status') cmp = a.status.localeCompare(b.status, 'pt-BR');
      else cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [workflows, sort]);

  // ── Selection ──
  const selectedCount = selectedIds.size;
  const allOnPageSelected = workflows.length > 0 && workflows.every((w) => selectedIds.has(w.id));
  const someOnPageSelected = workflows.some((w) => selectedIds.has(w.id));

  // F34: seleção em massa vive SÓ na view Lista (tabela) — cards sem checkbox.
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) workflows.forEach((w) => next.delete(w.id));
      else workflows.forEach((w) => next.add(w.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  // ── Filters ──
  const setSearchTerm = (term: string) => {
    setSearch(term);
    setPage(1);
  };

  const applyStatusFilter = (status: WorkflowStatus | '') => {
    setStatusFilter(status);
    setPage(1);
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setStatusFilter('');
    setPage(1);
    setFiltersOpen(false);
  };

  const statusLabel = statusFilter ? STATUS_FILTER_LABELS[statusFilter] ?? statusFilter : null;
  const activeFilterCount = statusLabel ? 1 : 0;

  // ── Folder handlers ──
  const handleSelectFolder = useCallback((id: string | null) => {
    setCurrentFolderId(id);
    setPage(1);
    if (id) navigate(`/automation/workflows/folder/${id}`, { replace: true });
    else navigate('/automation/workflows', { replace: true });
  }, [navigate]);

  const handleCreateFolder = useCallback(() => {
    setEditingFolder(null);
    setFolderModalOpen(true);
  }, []);

  const handleEditFolder = useCallback((folder: WorkflowFolderResponse) => {
    setEditingFolder(folder);
    setFolderModalOpen(true);
  }, []);

  const handleDeleteFolder = useCallback(async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja excluir esta pasta? Os workflows dentro dela serao movidos para a raiz.', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) {
      try {
        await deleteFolder.mutateAsync({ id, force: true });
        if (currentFolderId === id) setCurrentFolderId(null);
      } catch (err) {
        console.error('Error deleting folder:', err);
      }
    }
  }, [deleteFolder, currentFolderId]);

  const handleFolderSubmit = useCallback(async (formData: CreateWorkflowFolderRequest) => {
    if (editingFolder) {
      await updateFolder.mutateAsync({
        id: editingFolder.id,
        data: { name: formData.name, description: formData.description, color: formData.color },
      });
    } else {
      await createFolder.mutateAsync({ ...formData, usageType: 'automation', parentId: currentFolderId || undefined });
    }
  }, [editingFolder, updateFolder, createFolder, currentFolderId]);

  const handleDropWorkflow = useCallback(async (workflowId: string, targetFolderId: string) => {
    try {
      await updateWorkflow.mutateAsync({ id: workflowId, data: { folderId: targetFolderId } });
    } catch (err) {
      console.error('Error moving workflow to folder:', err);
    }
  }, [updateWorkflow]);

  const handleDropWorkflowToRoot = useCallback(async (workflowId: string) => {
    try {
      await updateWorkflow.mutateAsync({ id: workflowId, data: { folderId: null } });
    } catch (err) {
      console.error('Error moving workflow to root:', err);
    }
  }, [updateWorkflow]);

  // ── Workflow handlers ──
  // U1 (spec 2026-09-20 §3, D18 — reverte o #237): "Novo workflow" vai direto
  // pro editor, que abre sozinho o seletor de nós em modo "só gatilhos". A
  // pasta atual viaja em ?folderId (o editor a manda no POST do primeiro
  // Salvar) — NÃO copiar o defeito das skills, que perdem a pasta ao criar.
  const handleCreateNew = useCallback(() => {
    const query = currentFolderId ? `?folderId=${encodeURIComponent(currentFolderId)}` : '';
    navigate(`/automation/workflows/new${query}`);
  }, [navigate, currentFolderId]);

  // "+" central do bottom-nav mobile → novo workflow (mockup .m-fab).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); handleCreateNew(); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [handleCreateNew]);

  const handleEdit = (id: string) => navigate(`/automation/workflows/${id}`);

  const handleExportAll = async () => {
    try {
      // Exporta só a árvore de workflows (automation) — skills têm export próprio
      const file = await workflowsApi.exportAll('automation');
      if (file.workflows.length === 0) {
        toast.info('Nenhum workflow para exportar.');
        return;
      }
      downloadJson('workflows.json', file);
    } catch (err) {
      console.error('Error exporting workflows:', err);
      toast.error('Falha ao exportar workflows.');
    }
  };

  const handleExportOne = async (id: string) => {
    try {
      const file = await workflowsApi.exportOne(id);
      const name = file.workflows[0]?.name ?? 'workflow';
      downloadJson(slugifyFilename(name), file);
    } catch (err) {
      console.error('Error exporting workflow:', err);
      toast.error('Falha ao exportar o workflow.');
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const workflow = workflows.find((w) => w.id === id);
      await duplicateWorkflow.mutateAsync({ id, newName: workflow ? `${workflow.name} (Cópia)` : undefined });
    } catch (err) {
      console.error('Error duplicating workflow:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja excluir este workflow?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) {
      try {
        await deleteWorkflow.mutateAsync(id);
      } catch (err) {
        console.error('Error deleting workflow:', err);
      }
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await activateWorkflow.mutateAsync(id);
    } catch (err) {
      console.error('Error activating workflow:', err);
    }
  };

  const handlePause = async (id: string) => {
    try {
      await pauseWorkflow.mutateAsync(id);
    } catch (err) {
      console.error('Error pausing workflow:', err);
    }
  };

  // ── Bulk actions (mutations single em loop) ──
  const bulkActivate = () => {
    workflows.filter((w) => selectedIds.has(w.id) && w.status !== 'active').forEach((w) => activateWorkflow.mutate(w.id));
    clearSelection();
  };

  const bulkPause = () => {
    workflows.filter((w) => selectedIds.has(w.id) && w.status === 'active').forEach((w) => pauseWorkflow.mutate(w.id));
    clearSelection();
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} workflow(s) selecionado(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selectedIds).forEach((id) => deleteWorkflow.mutate(id));
    clearSelection();
  };

  const totalPages = data?.totalPages || 1;
  const currentPage = data?.page ?? page;

  // ── Reusable render helpers ──
  const renderCheck = (on: boolean, mixed = false, onClick?: () => void, label?: string, testId?: string) => (
    <button
      type="button"
      onClick={onClick}
      className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors flex-shrink-0"
      style={{
        backgroundColor: on || mixed ? 'var(--theme-primary)' : 'transparent',
        borderColor: on || mixed ? 'var(--theme-primary)' : 'var(--theme-input-border)',
        color: '#ffffff',
      }}
      aria-label={label}
      data-testid={testId}
    >
      {on && <Icon name="check" className="w-3 h-3" />}
      {!on && mixed && <span className="w-2 h-0.5 rounded-full bg-white" />}
    </button>
  );

  const SortHead: React.FC<{ label: string; k: SortKey; className?: string }> = ({ label, k, className }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-bold whitespace-nowrap cursor-pointer select-none ${className ?? ''}`}
      style={{ color: 'var(--theme-muted-text)' }}
      onClick={() => handleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === k && <Icon name={sort.dir === 'asc' ? 'chevron-up' : 'chevron-down'} className="w-3 h-3" />}
      </span>
    </th>
  );

  const cardHandlers = {
    onEdit: handleEdit,
    onDuplicate: handleDuplicate,
    onDelete: handleDelete,
    onActivate: handleActivate,
    onPause: handlePause,
    onExport: handleExportOne,
  };

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
      {STATUS_FILTER_OPTIONS.map(({ value, label }) => {
        const active = statusFilter === value;
        return (
          <button
            key={label}
            onClick={() => applyStatusFilter(value)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
            data-testid={`workflows-filter-status-${value || 'all'}`}
          >
            {label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={
          /* Esquerda: breadcrumb + contador */
          <>
          <nav className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate('/automation')}
              className="text-sm font-medium truncate hidden sm:inline"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid="workflows-breadcrumb-automation"
            >
              Automação
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Workflows</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada — F17-F22: ToolbarSearch, 360px) */
          <ToolbarSearch
            value={search}
            onChange={setSearchTerm}
            placeholder="Buscar workflow…"
            shrinkClass="ds-search"
            data-testid="workflows-search"
          />
        }
        right={
          /* Direita (ordem canônica): Filtros → Toggle → overflow → separador → CTA */
          <>
          {/* Filtros (F05: FiltersButton shared) */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="workflows-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-56 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="workflows-filters-popover"
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-F11: Lista primeiro, grid = "Cards") */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="workflows-view"
          />

          {/* Import / Export tudo (overflow) */}
          <div className="flex-none">
            <CardActionsMenu
              size="md"
              data-testid="workflows-more-actions"
              actions={[
                { label: 'Importar', icon: 'upload', onClick: () => setImportOpen(true) },
                { label: 'Exportar tudo', icon: 'download', onClick: handleExportAll },
              ]}
            />
          </div>

          <span className="w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Novo workflow (único primário — F01-F03: Button shared, rótulo colapsa via @container) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={handleCreateNew}
            data-testid="workflows-create-btn"
          >
            Novo workflow
          </Button>
          </>
        }
        mobile={
          /* `.m-sub` do mockup: busca + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={search}
              onChange={setSearchTerm}
              placeholder="Buscar workflow…"
              data-testid="workflows-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="workflows-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* Pastas (FolderList preservado) — folderbar acima da filterbar/bulk (mockup) */}
      <FolderList
        folders={displayFolders}
        currentFolder={currentFolder}
        breadcrumb={breadcrumb}
        isLoading={foldersLoading}
        onSelectFolder={handleSelectFolder}
        onCreateFolder={handleCreateFolder}
        onEditFolder={handleEditFolder}
        onDeleteFolder={handleDeleteFolder}
        onDropWorkflow={handleDropWorkflow}
        onDropWorkflowToRoot={handleDropWorkflowToRoot}
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => applyStatusFilter('')} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status" data-testid="workflows-chip-status-remove">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="workflows-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }} data-testid="workflows-bulk-bar">
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionado(s)</span>
          <span className="flex-1" />
          <button onClick={bulkActivate} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="workflows-bulk-activate">
            <Icon name="play" className="w-3.5 h-3.5" /> Ativar
          </button>
          <button onClick={bulkPause} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="workflows-bulk-pause">
            <Icon name="pause" className="w-3.5 h-3.5" /> Pausar
          </button>
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="workflows-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="workflows-bulk-clear">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {isLoading && <ListPageLoading message="Carregando workflows..." />}

          {!isLoading && error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar workflows: {(error as Error).message}</p>
            </div>
          )}

          {/* Empty: nenhum workflow E nenhuma pasta */}
          {!isLoading && !error && workflows.length === 0 && displayFolders.length === 0 && (
            <ListPageEmpty
              icon="zap"
              message="Nenhum workflow encontrado"
              description={search || activeFilterCount > 0
                ? 'Tente ajustar a busca ou os filtros.'
                : 'Workflows automatizam tarefas repetitivas e integram seus processos. Comece criando seu primeiro workflow.'}
              actionLabel="Criar workflow"
              onAction={handleCreateNew}
            />
          )}

          {/* Empty: há pastas mas nenhum workflow neste nível */}
          {!isLoading && !error && workflows.length === 0 && displayFolders.length > 0 && (
            <div className="text-center py-12">
              <Icon name="zap" className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--theme-muted-text)', opacity: 0.5 }} />
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                {currentFolder ? 'Nenhum workflow nesta pasta' : 'Nenhum workflow encontrado'}
              </p>
              <button onClick={handleCreateNew} className="text-sm mt-2" style={{ color: 'var(--theme-primary)' }} data-testid="workflows-empty-create">
                Criar workflow
              </button>
            </div>
          )}

          {/* Cards (grid) */}
          {!isLoading && !error && workflows.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
              {sortedWorkflows.map((workflow) => (
                <WorkflowCard
                  key={workflow.id}
                  workflow={workflow}
                  {...cardHandlers}
                />
              ))}
            </div>
          )}

          {/* Lista (tabela) — vira cards no mobile */}
          {!isLoading && !error && workflows.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedWorkflows.map((workflow) => (
                  <WorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    {...cardHandlers}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                {/* A caixa de fora é arredondada e RECORTA (`overflow-hidden`), então
                    quem rola é este invólucro — molde de LeadsListPage. Sem ele, as 2
                    colunas novas empurram a tabela para 1242px dentro de uma caixa de
                    958px (notebook de 1280px com a barra lateral aberta, medido) e
                    "Atualizado" + a coluna do ••• somem SEM barra de rolagem — o ••• é o
                    único caminho para Duplicar/Exportar/Excluir na view Lista. */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                        <th className="pl-4 pr-1 py-3 w-11">
                          {renderCheck(allOnPageSelected, someOnPageSelected && !allOnPageSelected, toggleSelectAllOnPage, 'Selecionar todos', 'workflows-select-all')}
                        </th>
                        <SortHead label="Workflow" k="name" />
                        <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Gatilho</th>
                        <SortHead label="Status" k="status" />
                        <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>Etapas</th>
                        {/* U2 / D19 — colunas simples de propósito: ordenar por última execução
                            no servidor está fora de escopo, e SortHead só aceita name/status/updated. */}
                        <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }} data-testid="workflows-col-last-execution">Última execução</th>
                        <th className="px-4 py-3 text-left text-xs font-bold whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }} data-testid="workflows-col-execution-counts">{`Execuções (${WORKFLOW_EXECUTION_STATS_WINDOW_DAYS} dias)`}</th>
                        <SortHead label="Atualizado" k="updated" />
                        <th className="px-4 py-3 w-14" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedWorkflows.map((workflow) => (
                        <WorkflowListItem
                          key={workflow.id}
                          workflow={workflow}
                          selected={selectedIds.has(workflow.id)}
                          onToggleSelect={toggleSelect}
                          {...cardHandlers}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Paginação */}
          {!isLoading && !error && workflows.length > 0 && (
            <ListPagePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => setPage(p)}
              totalItems={data?.total}
              pageSize={limit}
              itemLabel="workflows"
            />
          )}
        </div>
      </div>

      {/* Create/Edit Folder Modal */}
      <CreateFolderModal
        isOpen={folderModalOpen}
        onClose={() => {
          setFolderModalOpen(false);
          setEditingFolder(null);
        }}
        onSubmit={handleFolderSubmit}
        folder={editingFolder}
        parentId={currentFolderId}
      />

      {/* Import Workflow Modal */}
      {importOpen && (
        <ImportWorkflowModal
          isOpen={importOpen}
          onClose={() => setImportOpen(false)}
          currentFolderId={currentFolderId}
        />
      )}

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="workflows-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>
    </div>
  );
};

export default WorkflowsPage;
